/* tslint:disable:no-console */
import {Injectable, OnDestroy} from '@angular/core';
import {Activity} from '@docma-mm-core/activity/activity';
import {ActivityDbService} from '@docma-mm-core/activity/db/activity-db.service';
import {MultiActivity} from '@docma-mm-core/activity/multi-activity';
import {Attachment} from '@docma-mm-core/attachment/attachment';
import {
  AttachmentFileDbEntity,
  mapAttachmentSourceToEntity,
  mapGroupAttachmentSourceToEntity
} from '@docma-mm-core/attachment/attachment-db.entity';
import {AttachmentDbService} from '@docma-mm-core/attachment/attachment-db.service';
import {Group} from '@docma-mm-core/group/group';
import {ChangeState} from '@docma-mm-core/model/change-state/change-state';
import {ChangeStatusEnum} from '@docma-mm-core/model/change-state/change-status.enum';
import {SyncState} from '@docma-mm-core/model/sync-state/sync-state';
import {MmPatchOperation} from '@docma-mm-core/patch/mm-patch-operation';
import {SyncFileService} from '@docma-mm-core/sync/sync-file.service';
import {Fault} from './fault';
import {FaultPersistenceService} from './fault-persistence.service';
import {FaultOperationPathEnum} from './patch/fault-operation-path.enum';
import {FaultPatch} from './patch/fault-patch';
import {FaultPatchService} from './patch/fault-patch-service';
import {GroupDbService} from '@docma-mm-core/group/group-db.service';
import {FaultsEdit} from '@docma-mm-core/fault/faults-edit';
import {Guid} from 'guid-typescript';
import {ActivityEdit} from '@docma-mm-core/activity/activity-edit';
import {AddOperation, ReplaceOperation} from 'fast-json-patch/commonjs/core';
import {FaultData} from '@docma-mm-core/fault/fault-data';
import {Subject} from 'rxjs';
import {ActivityPatchModeEnum} from '@docma-mm-core/activity/activity-patch-mode.enum';
import {CurrentFaultsSyncStateService} from '../../mm-faults/current-faults-sync-state.service';

@Injectable()
export class FaultChangesCommitService implements OnDestroy {

  private activityEditCommittedSubject: Subject<{mydocmaUid: string, activityEdit: ActivityEdit}> = new Subject<{mydocmaUid: string, activityEdit: ActivityEdit}>();
  activityEditCommitted$ = this.activityEditCommittedSubject.asObservable();

  constructor(private readonly patchService: FaultPatchService,
              private readonly syncFileService: SyncFileService,
              private readonly activityDbService: ActivityDbService,
              private readonly attachmentDbService: AttachmentDbService,
              private readonly groupDbService: GroupDbService,
              private readonly persistenceService: FaultPersistenceService,
              private readonly currentFaultsSyncStateService: CurrentFaultsSyncStateService) {
  }

  ngOnDestroy(): void {
  }

  public async commitGroupChanges(mydocmaUid: string, group?: Group, multiActivity?: MultiActivity): Promise<Array<FaultPatch>> {
    const patches: { FaultPatches: Array<FaultPatch> } = { FaultPatches: [] };

    if (group) {
      AddPatches(await this.commitGroupChangesInternal(mydocmaUid, group), patches);
    }

    console.info('### committing activities to index db ###');

    AddPatches(await this.commitMultiActivityChanges(mydocmaUid, group, multiActivity), patches);

    // await Promise.all(multiActivity.entities.map(async entity => {
    //   AddPatches(await this.commitMultiActivityChangesOLD(mydocmaUid, group, entity.Activity, false, multiActivity), patches);
    // }));

    console.info('### finished committing activities to index db ###');

    if (multiActivity.entities && multiActivity.entities.length > 0 &&
        multiActivity.entities[0].Activity.attachments && multiActivity.entities[0].Activity.attachments.length > 0) {

      console.info('### committing attachments to index db ###');

      const activityInternal = multiActivity.entities[0].Activity;
      await Promise.all(activityInternal.attachments.map(async att => {
        AddPatches(await this.commitAttachmentChanges(mydocmaUid,
                                                 null,
                                                      multiActivity.entities[0].Activity,
                                                      group,
                                                      att,
                                                      multiActivity),
                                                      patches);
      }));

      console.info('### finished committing attachments to index db ###');
    }

    await this.currentFaultsSyncStateService.set(mydocmaUid);

    // let i = 0;
    // for (const entity of multiActivity.entities) {
    //   const commitGroup = (i === 0 ? true : false);
    //   i++;
    //   AddPatches(await this.commitMultiActivityChanges(mydocmaUid, group, entity.Activity, commitGroup, multiActivity), patches);
    // }

    return patches.FaultPatches;
  }

  public async commitFaultChanges(mydocmaUid: string,
                                  fault: Fault, commitGroup: boolean, group?: Group, multiActivity?: Activity): Promise<Array<FaultPatch>> {

    const patches: { FaultPatches: Array<FaultPatch> } = { FaultPatches: [] };


    if (fault.changeState.status !== ChangeStatusEnum.NotChanged) {
      let updateDate: Date;

      if (fault.syncState.error) {

        let errorFields: string[] = [];
        if (fault.syncState.error instanceof Object) {
          if ((fault.syncState.error?.InternalErrors?.length ?? 0) > 0) {
            errorFields = fault.syncState.error?.InternalErrors.map(x => x.Prop);
          }
          else {
            errorFields = JSON.parse(fault.syncState.error).InternalErrors.map(x => x.Prop);
          }
        }

        const existingPatches = await this.patchService.getByFaultUid(mydocmaUid, fault.uid);
        const f1 = existingPatches.findIndex(f => f.gUid != null);

        const patchTimes = existingPatches.slice(0, (f1 === -1 ? existingPatches.length : f1)).filter(x => {
          const opValue = (x.operation as AddOperation<any>)?.value ?? (x.operation as ReplaceOperation<any>)?.value;
          return (!x.gUid && (fault.changeState.status === ChangeStatusEnum.New || fault.changeState.status === ChangeStatusEnum.Changed)); // && errorFields.some(s => opValue.hasOwnProperty(s))
        }).map(o => o.date.getTime());

        // (x.operation.op === "add" || x.operation.op === "replace")1

        const data1 = new FaultData();
        const data2 = new FaultData();

        for (let key in fault.changeState.data) {
          if (errorFields.findIndex(f => f === key) === -1) {
            data2[key] = fault.changeState.data[key];
          }
          else {
            data1[key] = fault.changeState.data[key];
          }
        }

        updateDate = new Date((Math.max(... patchTimes) + 1000));

        const changeState1  = new ChangeState({ status: fault.changeState.status, date: fault.changeState.date, data: data1 });
        const changeState2  = new ChangeState({ status: fault.changeState.status, date: fault.changeState.date, data: data2 });

        // (fault.changeState.data as Array<any>).filter(x => x.propertyName)
        // fault.changeState.data

        const op1 = this.patchService.generateOperationFromChangeState(FaultOperationPathEnum.fault, changeState1);
        const op2 = this.patchService.generateOperationFromChangeState(FaultOperationPathEnum.fault, changeState2);


        const syncState1 = await this.patchService.save(mydocmaUid,
          [{fUid: fault.uid, oId: fault.id, date: (updateDate ? updateDate : changeState1.date), operation: op1 }]);

        const syncState2 = await this.patchService.save(mydocmaUid,
          [{fUid: fault.uid, oId: fault.id, date: changeState2.date, operation: op2 }]);

        AddPatches(syncState1, patches);
      }
      else {
        const op = this.patchService.generateOperationFromChangeState(FaultOperationPathEnum.fault, fault.changeState);

        AddPatches(await this.patchService.save(mydocmaUid,
          [{fUid: fault.uid, oId: fault.id, date: fault.changeState.date, operation: op }]), patches);
      }


      fault.syncState = this.addChangesToSyncState(fault.syncState, fault.changeState);
      await this.discardFaultChanges(mydocmaUid, fault);
    }

    for (const at of fault.attachments) {
      AddPatches(await this.commitAttachmentChanges(mydocmaUid, fault,  null, null, at), patches);
    }

    for (const ac of fault.activities) {
      AddPatches(await this.commitActivityChanges(mydocmaUid, fault, ac), patches);
    }

    await this.currentFaultsSyncStateService.set(mydocmaUid);

    return patches.FaultPatches;
  }

  private async commitGroupChangesInternal(mydocmaUid: string, group: Group): Promise<Array<FaultPatch>> {
    const patches: { FaultPatches: Array<FaultPatch> } = { FaultPatches: [] };

    if (group.changeState.status !== ChangeStatusEnum.NotChanged) {
      const op = this.patchService.generateOperationFromChangeState(FaultOperationPathEnum.group, group.changeState);

      AddPatches(await this.patchService.save(mydocmaUid,
        [{
          gUid: group.uid,
          gId: group.id,
          oId: group.id, oUid: group.uid,
          date: group.changeState.date, operation: op }]), patches);

      group.syncState = this.addChangesToSyncState(group.syncState, group.changeState);
      await this.discardGroupChanges(mydocmaUid, group);
    }
    return patches.FaultPatches;
  }

  private async commitMultiActivityChanges(mydocmaUid: string, group: Group, multiActivity?: MultiActivity): Promise<Array<FaultPatch>> {
    const savedPatches: { FaultPatches: Array<FaultPatch> } = { FaultPatches: [] };
    const patches: FaultPatch[] = [];
    const activities: Activity[] = [];

    const entities = (multiActivity.patchMode === ActivityPatchModeEnum.MultiActivity_Single ? multiActivity.entities.filter(e => e.IsInitActivity) : multiActivity.entities);
    // const groupEntities = (multiActivity.patchMode === ActivityPatchModeEnum.MultiActivity_Single ? multiActivity.entities.filter(e => !e.IsInitActivity) : []);

    const options: { [optionKey: string]: any } = {};
    options["ActivityPatchModeEnum"] = multiActivity.patchMode;

    for (const entity of entities) {
      const createDate = entity.Activity.changeState.date;

      if (entity.Activity.changeState.status !== ChangeStatusEnum.NotChanged) {
        const op = this.patchService.generateOperationFromChangeState(FaultOperationPathEnum.groupActivity, entity.Activity.changeState);

        patches.push({
          fUid: entity.Fault.uid,
          gUid: group.uid,
          gId:  group.id,
          oId: entity.Activity.id, oUid: entity.Activity.uid,
          date: entity.Activity.changeState.date,
          operation: op,
          options: options});

        entity.Activity.syncState = this.addChangesToSyncState(entity.Activity.syncState, entity.Activity.changeState);
        activities.push(entity.Activity);
      }
    }

    AddPatches(await this.patchService.saveAll(mydocmaUid, patches), savedPatches);
    await this.discardActivitiesChanges(mydocmaUid, activities);

    if (options && options["ActivityPatchModeEnum"]) {
      const activityEdit = new ActivityEdit();
      activityEdit.activities = activities;
      activityEdit.changes = savedPatches.FaultPatches[0].operation["value"];

      this.activityEditCommittedSubject.next({mydocmaUid: mydocmaUid, activityEdit: activityEdit});
    }

    await this.currentFaultsSyncStateService.set(mydocmaUid);

    return savedPatches.FaultPatches;
  }

  public async commitFaultsEditChanges(mydocmaUid: string, faultsEdit: FaultsEdit): Promise<Array<FaultPatch>> {
    const savedPatches: { FaultPatches: Array<FaultPatch> } = { FaultPatches: [] };
    const patches: FaultPatch[] = [];
    const faults: Fault[] = [];
    const faultUids = faultsEdit.loadedFauls.map(m => m.UniqueId);
    const gUid = Guid.create().toString();

    for (const fault of faultsEdit.loadedFauls) {
      const viewFault = faultsEdit.faults.get(fault.Id);

      /* set changeState data */
      viewFault.changeState.data = faultsEdit.changes;

      if (viewFault.changeState.status !== ChangeStatusEnum.NotChanged) {
        const op = this.patchService.generateOperationFromChangeState(FaultOperationPathEnum.faults, viewFault.changeState);

        patches.push({
          fUid: viewFault.uid,
          gUid,
          gId: null,
          oId: viewFault.id, oUid: viewFault.uid,
          date: viewFault.changeState.date, operation: op
        });

        viewFault.syncState = this.addChangesToSyncState(viewFault.syncState, viewFault.changeState);
        faults.push(viewFault);
      }
    }

    AddPatches(await this.patchService.saveAll(mydocmaUid, patches), savedPatches);
    await this.discardFaultsChanges(mydocmaUid, faults);

    await this.currentFaultsSyncStateService.set(mydocmaUid);

    return savedPatches.FaultPatches;
  }

  private async commitMultiActivityChangesOLD(mydocmaUid: string, group: Group, activity: Activity, commitGroup: boolean, multiActivity?: MultiActivity): Promise<Array<FaultPatch>> {
    const createDate = activity.changeState.date;

    const patches: { FaultPatches: Array<FaultPatch> } = { FaultPatches: [] };

    if (activity.changeState.status !== ChangeStatusEnum.NotChanged) {
      const op = this.patchService.generateOperationFromChangeState(FaultOperationPathEnum.groupActivity, activity.changeState);

      AddPatches(await this.patchService.save(mydocmaUid,
        [{
          fUid: activity.fault.uid,
          gUid: group.uid,
          gId:  group.id,
          oId: activity.id, oUid: activity.uid,
          date: activity.changeState.date, operation: op }]), patches);

      activity.syncState = this.addChangesToSyncState(activity.syncState, activity.changeState);
      await this.discardActivityChanges(mydocmaUid, activity.fault.uid, activity);
    }

    // if (commitGroup) {
    //   for (const at of activity.attachments) {
    //     AddPatches(await this.commitAttachmentChanges(mydocmaUid, null, activity, group, at, multiActivity), patches);
    //   }
    // }

    return patches.FaultPatches;
  }

  public async commitActivityChanges(mydocmaUid: string, fault: Fault, activity: Activity, commitGroup?: boolean): Promise<Array<FaultPatch>> {
    const patches: { FaultPatches: Array<FaultPatch> } = { FaultPatches: [] };

    if (activity.changeState.status !== ChangeStatusEnum.NotChanged) {
      const op = this.patchService.generateOperationFromChangeState(FaultOperationPathEnum.activity, activity.changeState);

      AddPatches(await this.patchService.save(mydocmaUid,
        [{
          fUid: fault.uid,
          oId: activity.id, oUid: activity.uid,
          pId: fault.id,
          date: activity.changeState.date, operation: op }]), patches);

      activity.syncState = this.addChangesToSyncState(activity.syncState, activity.changeState);
      await this.discardActivityChanges(mydocmaUid, fault.uid, activity);
    }

    for (const at of activity.attachments) {
      AddPatches(await this.commitAttachmentChanges(mydocmaUid, fault, activity, null, at), patches);
    }

    return patches.FaultPatches;
  }

  public async commitAttachmentChanges(mydocmaUid: string, fault: Fault, activity: Activity, group: Group, attachment: Attachment, multiActivity?: MultiActivity): Promise<Array<FaultPatch>> {
    if (attachment.changeState.status === ChangeStatusEnum.NotChanged) {
      return;
    }

    const patches: { FaultPatches: Array<FaultPatch> } = { FaultPatches: [] };
    let op: MmPatchOperation;
    let pId: number;
    let pUid: string;
    let gId: number;
    let gUid: string;
    if (fault && !activity) {
      // fault attachment
       op = this.patchService.generateOperationFromChangeState(FaultOperationPathEnum.attachment, attachment.changeState);
       pId =  fault.id ;
       pUid = fault.uid;
    } else if (group) {
      // activity group attachment
      op = this.patchService.generateOperationFromChangeState(FaultOperationPathEnum.groupAttachment, attachment.changeState);
      pId =  group.id;
      pUid = group.uid;
      gId =  group.id;
      gUid = group.uid;
    } else if (fault && activity) {
      // activity attachment
      op = this.patchService.generateOperationFromChangeState(FaultOperationPathEnum.activityAttachment, attachment.changeState);
      pId = activity.id;
      pUid = activity.uid;
    }

    AddPatches(await this.patchService.save(mydocmaUid, [{fUid: (fault ? fault.uid : null), pId, pUid, oId: attachment.id, oUid: attachment.uid, gId, gUid, date: attachment.changeState.date, operation: op }]), patches);

    if (op.op === 'add') {
      let attachmentFileEntity: AttachmentFileDbEntity;

      if (fault) {
        attachmentFileEntity = await mapAttachmentSourceToEntity(fault.uid, null, attachment);
      }
      else if (group) {
        attachmentFileEntity = await mapGroupAttachmentSourceToEntity(group, attachment);
      }

      const fileEntity = attachmentFileEntity.loadState.data;
      fileEntity.uid = attachment.uid;

      await this.syncFileService.saveFile(mydocmaUid, fileEntity);
    }
    attachment.syncState = this.addChangesToSyncState(attachment.syncState, attachment.changeState);

    if (fault) {
      await this.discardAttachmentChanges(mydocmaUid, fault?.uid, activity?.uid, attachment);
    }
    else if (group) {
      await this.discardGroupAttachmentChanges(mydocmaUid, group, activity?.uid, attachment, multiActivity);
    }

    return patches.FaultPatches;
  }

  public async discardFaultChanges(mydocmaUid: string, fault: Fault, deepDiscard: boolean = true) {
    if (fault.changeState.status !== ChangeStatusEnum.NotChanged) {
      fault.changeState = new ChangeState({status: ChangeStatusEnum.NotChanged});
      await this.persistenceService.saveChangeState(mydocmaUid, fault.uid, fault.changeState);
    }
  }

  public async discardFaultsChanges(mydocmaUid: string, faults: Fault[], deepDiscard: boolean = true) {
    const discardItems: string[] = [];

    for (const fault of faults) {
      if (fault.changeState.status === ChangeStatusEnum.NotChanged) {
        continue;
      }
      fault.changeState = new ChangeState({status: ChangeStatusEnum.NotChanged});
      discardItems.push(fault.uid);
    }

    await this.persistenceService.saveChangeStates(mydocmaUid, discardItems, new ChangeState({status: ChangeStatusEnum.NotChanged}));
  }

  public async discardAttachmentChanges(mydocmaUid: string, faultUid: string, activityUid: string, attachment: Attachment) {
    if (attachment.changeState.status === ChangeStatusEnum.NotChanged) {
      return;
    }
    attachment.changeState = new ChangeState({status: ChangeStatusEnum.NotChanged});
    await this.attachmentDbService.saveChangeState(mydocmaUid, faultUid, activityUid, null, attachment.uid, attachment.changeState);
  }

  public async discardGroupAttachmentChanges(mydocmaUid: string, group: Group, activityUid: string, attachment: Attachment, multiActivity: MultiActivity) {
    if (attachment.changeState.status === ChangeStatusEnum.NotChanged) {
      return;
    }
    attachment.changeState = new ChangeState({status: ChangeStatusEnum.NotChanged});
    for (const entity of multiActivity.entities) {
      await this.attachmentDbService.saveChangeState(mydocmaUid, entity.Fault.uid, entity.Activity.uid, group?.uid, attachment.uid, attachment.changeState);
    }
  }

  public async discardActivityChanges(mydocmaUid: string, faultUid: string, activity: Activity) {
    if (activity.changeState.status === ChangeStatusEnum.NotChanged) {
      return;
    }
    activity.changeState = new ChangeState({status: ChangeStatusEnum.NotChanged});
    await this.activityDbService.saveChangeState(mydocmaUid, faultUid, activity.uid, activity.changeState);
  }

  public async discardActivitiesChanges(mydocmaUid: string, activities: Activity[]) {
    const discardItems: { activityUid: string, faultUid: string }[] = [];

    for (const activity of activities) {
      if (activity.changeState.status === ChangeStatusEnum.NotChanged) {
        continue;
      }
      activity.changeState = new ChangeState({status: ChangeStatusEnum.NotChanged});
      discardItems.push({ activityUid: activity.uid, faultUid: activity.fault.uid });
    }
    await this.activityDbService.saveChangeStates(mydocmaUid, discardItems, new ChangeState({status: ChangeStatusEnum.NotChanged}));
  }

  // public async discardGroupActivityChanges(mydocmaUid: string, faultUids: string[], activity: Activity) {
  //   if (activity.changeState.status === ChangeStatusEnum.NotChanged) {
  //     return;
  //   }
  //   activity.changeState = new ChangeState({status: ChangeStatusEnum.NotChanged});
  //   await this.activityDbService.saveChangeStates(mydocmaUid, faultUids, activity.uid, activity.changeState);
  // }

  public async discardGroupChanges(mydocmaUid: string, group: Group) {
    if (group.changeState.status === ChangeStatusEnum.NotChanged) {
      return;
    }
    group.changeState = new ChangeState({status: ChangeStatusEnum.NotChanged});
    await this.groupDbService.saveChangeState(mydocmaUid, group.uid, group.changeState);
  }

  private addChangesToSyncState(syncState: SyncState, changeState: ChangeState<any>): SyncState {
    const newSyncState = new SyncState(syncState);
    const syncChanges = [...syncState.changes ?? []];
    syncChanges.push(new ChangeState(changeState));
    newSyncState.changes = syncChanges;
    return newSyncState;
  }
}

function AddPatches(params: FaultPatch[], patches: { FaultPatches: Array<FaultPatch> }) {
  if (params) {
    patches.FaultPatches = patches.FaultPatches.concat(params);
  }
}
